import { FlowStores } from "./flowLineRelocation";

// A copy of everything the flow lines have counted, so it can outlive the things that used to destroy it: a trip into playback (which has its own counts for
// its own day) and a page refresh. The numbers are plain maps, so a snapshot is plain arrays of [key, value].
export interface FlowSnapshot {
  pathWeights: Array<[string, number]>;
  directionalPathWeights: Array<[string, number]>;
  pathWeightLabels: Array<[string, string]>;
  parked: {
    pairs: Array<[string, number]>;
    directional: Array<[string, number]>;
    arrivals: Array<[string, number]>;
  };
}

export const takeFlowSnapshot = (s: FlowStores): FlowSnapshot => ({
  pathWeights: Array.from(s.pathWeights.entries()),
  directionalPathWeights: Array.from(s.directionalPathWeights.entries()),
  pathWeightLabels: Array.from(s.pathWeightLabels.entries()),
  parked: {
    pairs: Array.from(s.parked.pairs.entries()),
    directional: Array.from(s.parked.directional.entries()),
    arrivals: Array.from(s.parked.arrivals.entries()),
  },
});

export const isEmptySnapshot = (snap: FlowSnapshot): boolean =>
  !snap.pathWeights.length &&
  !snap.directionalPathWeights.length &&
  !snap.parked.pairs.length &&
  !snap.parked.directional.length &&
  !snap.parked.arrivals.length;

/** Replaces the contents of the stores (the same Map objects: the rest of the code holds references to them). */
export const applyFlowSnapshot = (s: FlowStores, snap: FlowSnapshot): void => {
  const fill = <V>(map: Map<string, V>, entries: Array<[string, V]>) => {
    map.clear();
    entries.forEach(([k, v]) => map.set(k, v));
  };
  fill(s.pathWeights, snap.pathWeights);
  fill(s.directionalPathWeights, snap.directionalPathWeights);
  fill(s.pathWeightLabels, snap.pathWeightLabels);
  fill(s.parked.pairs, snap.parked.pairs);
  fill(s.parked.directional, snap.parked.directional);
  fill(s.parked.arrivals, snap.parked.arrivals);
};

const numPairs = (x: unknown): Array<[string, number]> | null =>
  Array.isArray(x) && x.every((e) => Array.isArray(e) && e.length === 2 && typeof e[0] === "string" && typeof e[1] === "number" && Number.isFinite(e[1]) && e[1] >= 0)
    ? (x as Array<[string, number]>)
    : null;
const strPairs = (x: unknown): Array<[string, string]> | null =>
  Array.isArray(x) && x.every((e) => Array.isArray(e) && e.length === 2 && typeof e[0] === "string" && typeof e[1] === "string")
    ? (x as Array<[string, string]>)
    : null;

/** A snapshot read back from storage is only trusted if every part has the right shape; anything else is dropped rather than half-applied. */
export const validateFlowSnapshot = (x: any): FlowSnapshot | null => {
  if (!x || typeof x !== "object") return null;
  const pathWeights = numPairs(x.pathWeights);
  const directionalPathWeights = numPairs(x.directionalPathWeights);
  const pathWeightLabels = strPairs(x.pathWeightLabels);
  const pairs = numPairs(x.parked?.pairs);
  const directional = numPairs(x.parked?.directional);
  const arrivals = numPairs(x.parked?.arrivals);
  if (!pathWeights || !directionalPathWeights || !pathWeightLabels || !pairs || !directional || !arrivals) return null;

  return { pathWeights, directionalPathWeights, pathWeightLabels, parked: { pairs, directional, arrivals } };
};

// Saved counts are for THIS site and for recent traffic. A day old is a different picture, so past this they are not brought back.
export const FLOW_STATE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface SavedFlowState {
  siteId: string;
  savedAt: number;
  snapshot: FlowSnapshot;
}

export const readSavedFlowState = (raw: unknown, siteId: string, nowMs: number): FlowSnapshot | null => {
  let parsed: any = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object" || !siteId || parsed.siteId !== siteId) return null;
  const savedAt = Number(parsed.savedAt);
  if (!Number.isFinite(savedAt) || nowMs - savedAt > FLOW_STATE_MAX_AGE_MS || savedAt > nowMs + 60_000) return null;
  const snap = validateFlowSnapshot(parsed.snapshot);

  return snap && !isEmptySnapshot(snap) ? snap : null;
};
